import React, { useMemo } from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import type { AdminEventLogItem } from 'api/admin/eventLogs/listEventLogs'
import { useEventLogsQuery } from 'queries/admin/eventLogs/useEventLogsQuery'
import { useEventLogsFiltersQueryState } from '../hooks/useEventLogsFiltersQueryState'
import { buildListEventLogsParams } from '../buildListEventLogsParams'
import type { FiltersFormValues } from '../filtersSchema'

type EventLogsContextState = {
  state: {
    filterValues: FiltersFormValues
    items: AdminEventLogItem[]
    hasNextPage: boolean
    isPending: boolean
    isFetchingNextPage: boolean
    isError: boolean
    errorMessage: string | undefined
    awaitingFiltersMessage: string | undefined
  }
  actions: {
    applyFilters: (values: FiltersFormValues) => void
    loadMore: () => void
  }
}

const [Context, useEventLogsContextValue] =
  createContext<EventLogsContextState>({
    name: 'EventLogsContext',
  })

export const EventLogsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [filterValues, setFilterValues] = useEventLogsFiltersQueryState()

  const listInput = useMemo(
    () => buildListEventLogsParams(filterValues),
    [filterValues]
  )

  const {
    data,
    isPending,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useEventLogsQuery(listInput.ok ? listInput.params : null)

  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data])

  const value: EventLogsContextState = {
    state: {
      filterValues,
      items,
      hasNextPage: Boolean(hasNextPage),
      // `useInfiniteQuery` reports `isPending: true` while disabled, so gate on filter validity to avoid a Loading flash before any narrowing field is supplied.
      isPending: listInput.ok && isPending,
      isFetchingNextPage,
      isError: listInput.ok && isError,
      errorMessage:
        parseErrorMessage(error) ||
        (isError ? 'Failed to load event logs.' : undefined),
      awaitingFiltersMessage: listInput.ok ? undefined : listInput.message,
    },
    actions: {
      applyFilters: setFilterValues,
      loadMore: () => {
        void fetchNextPage()
      },
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useEventLogsContextValue }
